import React, { useMemo, useRef } from "react";
import { Autocomplete, useLoadScript } from "@react-google-maps/api";

const libraries = ["places"];

export default function AddressAutocomplete({ value, onTyping, onSelect }) {
  const acRef = useRef(null);

  // Load the Places library, but render a normal input even before it's ready
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries,
  });

  const options = useMemo(
    () => ({
      componentRestrictions: { country: "us" },
      fields: [
        "place_id",
        "name",
        "formatted_address",
        "address_components",
        "geometry",
      ],
    }),
    []
  );

  const handlePlaceChanged = () => {
    const place = acRef.current?.getPlace();
    if (!place || !place.place_id) return;

    const comps = place.address_components || [];
    const get = (t) =>
      comps.find((c) => c.types.includes(t))?.long_name || "";

    const city =
      get("locality") ||
      get("sublocality") ||
      get("administrative_area_level_3") ||
      get("administrative_area_level_2") ||
      "";
    const zip = get("postal_code") || "";

    const lat = place.geometry?.location?.lat?.();
    const lng = place.geometry?.location?.lng?.();

    const label = place.name
      ? `${place.name} | ${place.formatted_address || ""}`.trim()
      : place.formatted_address || value || "";

    onSelect({
      inputLabel: label,
      location_name: place.name || place.formatted_address || "",
      formatted_address: place.formatted_address || "",
      city,
      zip_code: zip,
      place_id: place.place_id,
      lat,
      lng,
    });
  };

  const Input = (
    <input
      type="text"
      value={value}
      onChange={(e) => onTyping(e.target.value)}
      placeholder="Start typing an address or facility…"
      className="w-full rounded border px-3 py-2"
      aria-label="Court/Location Name"
    />
  );

  // Before script loads: show a normal input (no "Loading…" text)
  // After loaded: wrap the same input with <Autocomplete> to enable suggestions
  return isLoaded ? (
    <Autocomplete
      onLoad={(ac) => (acRef.current = ac)}
      onPlaceChanged={handlePlaceChanged}
      options={options}
    >
      {Input}
    </Autocomplete>
  ) : (
    Input
  );
}
